/**
 * The EMBEDDED shell (2026-09): Kola as a native page inside the Rainvic
 * Tool desktop app. The desktop renders `<EmbeddedKolaApp>` where its old
 * `<webview>` used to be, hands it a `KolaHost` (where the server is, how to
 * get the signed-in user's ID token, how to open a link outside, its theme)
 * and a route, and hears back whenever Kola navigates.
 *
 * What is different from the web shell (main.tsx / App.tsx), and only that:
 *  - no sign-in screen of its own: the host's Firebase user is THE user, so
 *    the gate resolves `/api/me` with the host's token and adopts the
 *    server-verified identity (App.tsx's FirebaseGate does the same after
 *    its cookie mint — this shell has no cookie and needs none);
 *  - `core/firebase.ts` is never imported: it initialises Firebase's
 *    [DEFAULT] app with Kola's config, and the desktop already has one with
 *    a different config — a second `initializeApp` would throw;
 *  - a MemoryRouter instead of the BrowserRouter: the host owns the real
 *    URL. `RouteFollower` turns each host request into a navigation;
 *    `RouteReporter` reports Kola's location back;
 *  - the theme STARTS as the host's (core/theme.ts `setDefaultTheme`) —
 *    the toggle stays and a reader's choice is remembered, as on the web —
 *    and the title is the host's (core/host.ts `setDocumentTitle` is a no-op);
 *  - global error capture stays off: patching `console.error` would swallow
 *    the whole desktop app's console into Kola's debug panel;
 *  - the wire contract is enforced (shared/contract.ts): a build the server
 *    no longer serves gets an "update the app" screen, not a broken board.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { MemoryRouter, useNavigate } from "react-router-dom";

import { DevGate, KolaRoutes } from "./App.js";
import { ApiError, fetchAuthMode, fetchMe, type AuthModeValue } from "./core/api.js";
import { installHost, type KolaHost } from "./core/host.js";
import { setActiveIdentity } from "./core/identity.js";
import { RouteReporter } from "./core/RouteReporter.js";
import { useServerInfo } from "./core/server-info.js";
import { setDefaultTheme } from "./core/theme.js";
import { loadAiModels } from "./features/ai-models/load.js";
import { DebugLogPanel } from "./features/debug/DebugLogPanel.js";
import { loadPresenterPhotos } from "./features/presenter-photos/load.js";
import { loadTextKinds } from "./features/text-kinds/load.js";
import { KolaRoot } from "./ui/KolaRoot.js";
import { VersionBadge } from "./ui/VersionBadge.js";
import "./ui/tokens.css";
import "./ui/app.css";
import { KOLA_API_CONTRACT } from "@kola/shared";

export type {
  HostDashboardView,
  HostDashboardViewContext,
  HostFileBadge,
  HostFileEntry,
  HostFileFolder,
  HostFileFoldersResult,
  HostFileListResult,
  HostFileReadResult,
  HostFileSource,
  HostPlaceVideoResult,
  KolaHost,
} from "./core/host.js";

/**
 * Where the host wants Kola to be. `path` is a router path (`/`, `/b/<id>`,
 * `/b/<id>?panel=thumbnails&cell=r:c`); `requestId` changes on every request
 * so the same path can be asked for twice (a second click on the same link
 * re-frames the cell after the first strip its params).
 */
export interface EmbeddedRoute {
  path: string;
  requestId: number;
}

export interface EmbeddedKolaAppProps {
  host: KolaHost;
  route: EmbeddedRoute;
  /** Kola navigated (a board opened from its dashboard, a link, the bell). */
  onRouteChange?: ((path: string) => void) | undefined;
}

export function EmbeddedKolaApp({ host, route, onRouteChange }: EmbeddedKolaAppProps) {
  // The host must be installed before any child renders or fetches, and a
  // lazy initializer runs during THIS component's first render — before its
  // children exist. Idempotent, so StrictMode's double invocation is fine.
  useState(() => {
    installHost(host);
    setDefaultTheme(host.theme);
    return null;
  });
  useEffect(() => {
    installHost(host);
    setDefaultTheme(host.theme);
  }, [host]);
  useEffect(
    () => () => {
      installHost(null);
      setDefaultTheme(null);
    },
    [],
  );

  return (
    <KolaRoot>
      <EmbeddedGate host={host}>
        <MemoryRouter initialEntries={[route.path]}>
          <RouteFollower route={route} />
          <RouteReporter onChange={onRouteChange} />
          <KolaRoutes />
        </MemoryRouter>
      </EmbeddedGate>
      <VersionBadge />
      <DebugLogPanel />
    </KolaRoot>
  );
}

/** Navigate whenever the host asks (a new `requestId`), even to the same path. */
function RouteFollower({ route }: { route: EmbeddedRoute }) {
  const navigate = useNavigate();
  const seen = useRef(route.requestId);
  useEffect(() => {
    if (seen.current === route.requestId) return;
    seen.current = route.requestId;
    navigate(route.path);
  }, [route.path, route.requestId, navigate]);
  return null;
}

type GatePhase = "loading" | "ready" | "signed-out" | "failed";

/**
 * Mirrors App.tsx's gates for a host that is already signed in: resolve the
 * auth mode, adopt the server-verified identity (firebase) or fall back to
 * the dev display-name prompt (dev/iap), then load the two admin registries
 * every screen reads.
 */
function EmbeddedGate({ host, children }: { host: KolaHost; children: ReactNode }) {
  const [mode, setMode] = useState<AuthModeValue | null>(null);
  const [phase, setPhase] = useState<GatePhase>("loading");
  const [attempt, setAttempt] = useState(0);
  const { clientOutdated, minClientContract, serverVersion } = useServerInfo();

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    void (async () => {
      const resolved = await fetchAuthMode();
      if (cancelled) return;
      setMode(resolved);
      // product: the product's own mode authenticates like firebase (a bearer token, /api/me names the user).
      if (resolved !== "firebase" && resolved !== "product") {
        setPhase("ready");
        return;
      }
      try {
        setActiveIdentity(await fetchMe());
        if (!cancelled) setPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setPhase(err instanceof ApiError && err.status === 401 ? "signed-out" : "failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (phase !== "ready") return;
    void loadTextKinds();
    void loadAiModels();
    // The presenter photo library rides the same schedule as the two admin
    // registries: the thumbnail panel needs it to know where the face comes from.
    void loadPresenterPhotos();
  }, [phase]);

  if (clientOutdated) {
    return (
      <div className="name-prompt">
        <div className="name-prompt-card">
          <h1>Update Rainvic Tool</h1>
          <p>
            This version of the app is too old for the Kola server
            {serverVersion === null ? "" : ` (v${serverVersion})`}. Install the update to keep
            using Kola.
          </p>
          <p style={{ opacity: 0.7 }}>
            Client contract {KOLA_API_CONTRACT}, server requires {minClientContract ?? "?"}.
          </p>
          {host.checkForUpdates !== undefined && (
            <button type="button" className="btn btn-primary" onClick={() => host.checkForUpdates?.()}>
              Check for updates
            </button>
          )}
        </div>
      </div>
    );
  }
  if (phase === "loading") return null;
  if (phase === "signed-out") {
    return (
      <div className="name-prompt">
        <div className="name-prompt-card">
          <h1>Sign in first</h1>
          <p>Kola uses your Rainvic Tool account — sign in to the app to open your boards.</p>
        </div>
      </div>
    );
  }
  if (phase === "failed") {
    return (
      <div className="name-prompt">
        <div className="name-prompt-card">
          <h1>Kola is unreachable</h1>
          <p>The board server did not answer. Check your connection, then try again.</p>
          <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  return mode === "firebase" || mode === "product" ? <>{children}</> : <DevGate>{children}</DevGate>;
}
